import { Injectable } from '@angular/core';
import * as THREE from 'three';
import { WorkbenchService } from '../../../../../services/configurators-2d/applications/workbench.service'; // REMOVED
import { ConfigurationPlan } from '../../../../../models/configurators-2d/applications/workbench/configurations/configuration-plan';
import { ShapeTypeEnum } from 'src/app/enum/workbench/shape-type.enum';

@Injectable({
  providedIn: 'root'
})
export class WorkbenchTextureService {
  private textureCache: Map<number, THREE.Texture> = new Map();
  private texturePathCache: Map<number, string> = new Map();

  constructor() { }

  public getTexture(planId: number): THREE.Texture | undefined {
    return this.textureCache.get(planId);
  }

  public getTexturePath(planId: number): string | undefined {
    return this.texturePathCache.get(planId);
  }

  public clearCache() {
    this.textureCache.clear();
    this.texturePathCache.clear();
  }

  public loadImage(imagePath: string, planId: number, scene: THREE.Scene, apiUrl: string, onApply?: (texture: THREE.Texture) => void) {
    this.texturePathCache.set(planId, imagePath);

    const fullPath = imagePath.startsWith('http') ? imagePath : apiUrl + imagePath;
    // console.log('Loading Texture:', fullPath);

    const loader = new THREE.TextureLoader();
    loader.load(fullPath, (texture) => {
      // Set wrapping BEFORE cloning in applyTextureToMesh so clones inherit RepeatWrapping
      texture.wrapS = THREE.RepeatWrapping;
      texture.wrapT = THREE.RepeatWrapping;
      texture.repeat.set(1, 1);
      texture.needsUpdate = true;

      // Find Plan to get dimensions for Repeat (Logic moved from component)
      const obj = scene.children.find(c => c.userData['id'] === planId);

      if (obj) {
        const plan = obj.userData['plan'];
        if (plan) {
          // We might reference plan here to update its internal path if needed
          plan.texturePath = imagePath;
        }

        // Apply immediately if object exists
        this.applyTextureToMesh(obj, texture);
      }

      this.textureCache.set(planId, texture);

      if (onApply) onApply(texture);
    });
  }

  public applyTextureToMesh(group: THREE.Object3D, texture: THREE.Texture) {
    // 1. Apply to Main Mesh (Top Face)
    const mainMesh = group.children.find(c => c.type === 'Mesh' && !c.userData['type']) as THREE.Mesh;
    if (mainMesh) {
      if (Array.isArray(mainMesh.material)) {
        // Clone texture for Main Mesh because it uses ExtrudeGeometry (World UVs)
        const mainTex = texture.clone();
        mainTex.needsUpdate = true;

        // Calculate Repeat based on Plan Dimensions
        const plan = group.userData['plan'];
        if (plan) {
          let w = plan.length || 1000;
          let h = plan.width || 600;

          // Adjust for circular shapes that rely on diameter
          if (plan.shape && (plan.shape.shapeType === ShapeTypeEnum.Circle || plan.shape.shapeType === ShapeTypeEnum.Mushroom || plan.shape.shapeType === ShapeTypeEnum.HalfMoon)) {
            const diameter = plan.diameter || (plan.shape && plan.shape.diameter) || plan.width;
            if (diameter) {
              w = diameter;
              h = plan.shape.shapeType === ShapeTypeEnum.Mushroom || plan.shape.shapeType === ShapeTypeEnum.HalfMoon ? plan.width || diameter : diameter;
            }
          }

          if (plan.shape && plan.shape.shapeType === ShapeTypeEnum.Circle) {
            mainTex.repeat.set(1, 1);
            mainTex.offset.set(0, 0);
          } else {
            mainTex.repeat.set(1 / w, 1 / h);
            mainTex.offset.set(0, 0); // UVs usually start at 0,0 for the shape
          }
        } else {
          // Fallback to bounding box
          group.updateMatrixWorld();
          const box = new THREE.Box3().setFromObject(mainMesh);
          const size = new THREE.Vector3();
          box.getSize(size);
          if (size.x > 0 && size.y > 0) {
            mainTex.repeat.set(1 / size.x, 1 / size.y);
          }
        }

        const mats = mainMesh.material as THREE.MeshBasicMaterial[];
        // Extrude geometry usually has top/bottom on index 0, sides on index 1
        // Cylinder geometry has sides on index 0, top on index 1, bottom on index 2
        // To be safe, look at our constructor in MeshBuilder - we pass [matTop, matSide] or [matSide, matTop, matTop]
        // But applying to all that are NOT gray color explicitly mapped as side is tedious.
        // Actually, in MeshBuilder we assigned 'matTop' as the material we want to texture.
        // We can just iterate over all materials that form the array, but we only really want to texture the top.
        // Since we explicitly constructed the array, let's just apply the texture to any material that is meant to be the top.
        // In our builder: Extrude [matTop, matSide], Cylinder [matSide, matTop, matTop]
        // We can check equality or simply map all of them except if we find a way to distinguish.
        // But wait! If we map 'matTop' which is shared, updating one updates all instances of matTop!
        // The issue is simply that we were only setting `mats[0].map = mainTex`.
        if (mats && mats.length > 0) {
          mats.forEach((mat, index) => {
            // In MeshBuilder, matSide was index 1 for Extrude, and index 0 for Cylinder.
            // Let's just apply it to ALL materials in the array for now. Since the sidewall
            // material will get the texture but it has weird UVs, it might show artifacts...
            // Wait, we WANT the side to be 0xcccccc without map.
            // Let's identify the 'matTop' by checking if it already has a map? No, it might be the first time.
            const plan = group.userData['plan'];
            const isCylinder = plan && plan.shape && plan.shape.shapeType === ShapeTypeEnum.Circle;

            let isTopMaterial = false;
            if (isCylinder) {
              isTopMaterial = (index === 1 || index === 2); // Top and Bottom of cylinder
            } else {
              isTopMaterial = (index === 0); // Top and Bottom of extrude
            }

            if (isTopMaterial) {
              mat.map = mainTex;
              mat.color.setHex(0xffffff);
              mat.needsUpdate = true;
            }
          });
        }
      } else {
        // Fallback for non-extrude?
        const mat = mainMesh.material as THREE.MeshBasicMaterial;
        // Check geometry type logic... assume same behavior or use texture directly if standard geo
        // For safety, let's assume world UVs if it's the Plan Mesh
        const plan = group.userData['plan'];
        if (plan) {
          const w = plan.length || 1000;
          const h = plan.width || 600;
          const clone = texture.clone();
          clone.repeat.set(1 / w, 1 / h);
          mat.map = clone;
        } else {
          mat.map = texture;
        }

        mat.color.setHex(0xffffff);
        mat.needsUpdate = true;
      }
    }

    // 2. Apply to Edge Strips
    const strips = group.children.filter(c => c.userData && c.userData['type'] === 'edgeStrip') as THREE.Mesh[];
    strips.forEach(strip => {
      // Clone texture for independent repeat
      const edgeTex = texture.clone();
      edgeTex.needsUpdate = true;

      // Calculate dimensions for repeat from userData
      // const edgeLen = strip.userData['originalLength'] || 1000; // Unused

      edgeTex.repeat.set(1, 1);

      const mat = strip.material as THREE.MeshBasicMaterial;
      mat.map = edgeTex;
      mat.color.setHex(0xffffff);
      mat.needsUpdate = true;
    });

  }
}
